import { useCallback, useEffect, useMemo, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { PortalHeader } from "../../../components/molecules/Header";
import { useNotification } from "../../../hooks/useNotification";
import { useGetSalesOrderFulfilmentStatusQuery } from "../../../store/apiSlice/salesApiSlice";
import {
  useCompleteFulfilmentBatchMutation,
  usePreviewFulfilmentBatchMutation,
} from "../../../store/apiSlice/salesFulfilmentApiSlice";
import { fulfilmentBatchBody } from "../layout/fulfilmentSelectionHelpers";
import { Tab, TabBar, TabList } from "../../shared/orderDesign/detailTabs";
import { DRAWER_WIDTH } from "../../shared/orderDesign/drawerWidths";
import { OrderDrawerShell } from "../../shared/orderDesign/OrderDrawerShell";
import {
  ADD_ITEMS_DRAWER_ROOT_CLASS,
  AddItemsPickerButton,
  AddItemsPickerContent,
  AddItemsPickerFooter,
  AddItemsPickerTheme,
  PO_ADD_ITEMS_DRAWER_ROOT_CLASS,
  PoDrawerButtonStyle,
  addItemsDrawerPanelStyles,
} from "../../shared/orderLineForm/components/addItemsPickerStyles";
import { FulfilFooterContext } from "./fulfil/FulfilParts";
import { AllocateStep } from "./fulfil/steps/AllocateStep";
import { InvoiceStep } from "./fulfil/steps/InvoiceStep";
import { PackStep } from "./fulfil/steps/PackStep";
import { PickStep } from "./fulfil/steps/PickStep";
import { ShipStep } from "./fulfil/steps/ShipStep";

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/**
 * The five steps, in order. `waiting` turns the status counts into a short
 * line for the step card.
 *
 * @const {!Array<!Object>}
 */
export const FULFIL_STEPS = [
  {
    key: "allocate",
    title: "Allocate",
    description: "Reserve stock for the order lines.",
    count: counts => counts.to_allocate,
    waiting: counts => plural(counts.to_allocate, "line", "lines") + " to allocate",
    Panel: AllocateStep,
  },
  {
    key: "pick",
    title: "Pick",
    description: "Create a pick list and record what was picked.",
    count: counts => counts.to_pick_list + counts.to_pick,
    waiting: counts =>
      `${plural(counts.to_pick_list, "line", "lines")} for a pick list · ${counts.to_pick} to pick`,
    Panel: PickStep,
  },
  {
    key: "pack",
    title: "Pack",
    description: "Put picked goods into packs.",
    count: counts => counts.to_pack,
    waiting: counts => plural(counts.to_pack, "line", "lines") + " to pack",
    Panel: PackStep,
  },
  {
    key: "ship",
    title: "Ship",
    description: "Put ready packs on a delivery and shipment.",
    count: counts => counts.to_ship,
    waiting: counts => plural(counts.to_ship, "pack", "packs") + " ready to ship",
    Panel: ShipStep,
  },
  {
    key: "invoice",
    title: "Invoice",
    description: "Invoice what's left to bill.",
    count: counts => counts.to_invoice,
    waiting: counts => plural(counts.to_invoice, "line", "lines") + " to invoice",
    Panel: InvoiceStep,
  },
];

const ROOT_CLASS = "so-fulfil-drawer";

const panelStyles = {
  ...addItemsDrawerPanelStyles,
  body: { padding: 0, overflow: "auto", display: "flex", flexDirection: "column" },
};

/**
 * Fulfil drawer on the sales order page or list, built on the same shell as
 * the purchase order Add items drawer.
 *
 * @param {{
 *   orderId?: (string|number),
 *   orderIds?: Array<string|number>,
 *   open: boolean,
 *   onClose: function(),
 * }} props
 */
export function FulfilWizard({ orderId, orderIds: orderIdsProp, open, onClose }) {
  const notify = useNotification();
  const orderIds = useMemo(() => {
    if (orderIdsProp?.length) {
      return orderIdsProp.map(id => String(id));
    }
    if (orderId != null && orderId !== "") {
      return [String(orderId)];
    }
    return [];
  }, [orderId, orderIdsProp]);
  const isMulti = orderIds.length > 1;
  const primaryOrderId = orderIds.length === 1 ? orderIds[0] : null;

  const [activeKey, setActiveKey] = useState(null);
  const [footerNode, setFooterNode] = useState(null);

  const [previewBatch, { data: batchData, isLoading: batchLoading }] =
    usePreviewFulfilmentBatchMutation();
  const [completeBatch, { isLoading: completing }] = useCompleteFulfilmentBatchMutation();

  const {
    data: singleData,
    isFetching: singleFetching,
    refetch: refetchSingle,
  } = useGetSalesOrderFulfilmentStatusQuery(primaryOrderId, {
    skip: !open || !primaryOrderId,
  });

  const loadBatchPreview = useCallback(() => {
    if (orderIds.length) {
      previewBatch(fulfilmentBatchBody(orderIds));
    }
  }, [orderIds, previewBatch]);

  useEffect(() => {
    if (open && isMulti) {
      loadBatchPreview();
    }
    if (!open) {
      setActiveKey(null);
    }
  }, [open, isMulti, loadBatchPreview]);

  useEffect(() => {
    if (open && primaryOrderId) {
      refetchSingle();
    }
  }, [open, primaryOrderId, refetchSingle]);

  const policy = isMulti ? batchData?.policy : singleData?.policy;
  const counts = isMulti ? batchData?.counts : singleData?.counts;
  const visibleSteps = useMemo(() => {
    if (policy?.show_invoice_step === false) {
      return FULFIL_STEPS.filter(step => step.key !== "invoice");
    }
    return FULFIL_STEPS;
  }, [policy]);

  const isFetching = isMulti ? batchLoading && !batchData : singleFetching && !singleData;
  const nextUp = counts ? visibleSteps.find(step => step.count(counts) > 0)?.key : null;
  const activeIndex = visibleSteps.findIndex(step => step.key === activeKey);
  const activeStep = visibleSteps[activeIndex];
  const goToNext = () => setActiveKey(visibleSteps[activeIndex + 1]?.key ?? null);

  const waitingText = step => {
    if (!counts) return null;
    return step.count(counts) > 0 ? step.waiting(counts) : "Nothing waiting";
  };

  const drawerTitle = isMulti ? "Fulfill selected orders" : "Fulfill this order";

  const handleCompleteRemaining = async () => {
    try {
      await completeBatch(fulfilmentBatchBody(orderIds)).unwrap();
      notify({
        type: "success",
        message: "Complete remaining ran for the selected sales orders.",
      });
      if (isMulti) {
        loadBatchPreview();
      } else {
        refetchSingle();
      }
    } catch (error) {
      notify({
        type: "error",
        message: error?.data?.detail || "Complete remaining could not finish every order.",
      });
    }
  };

  const showCompleteRemaining =
    !activeStep && policy?.sale_fulfilment_mode === "one_click" && orderIds.length > 0;

  const renderStepPanels = () => {
    if (!activeStep) return null;
    if (isMulti) {
      return orderIds.map(id => {
        const meta = batchData?.orders?.find(row => String(row.order_id) === String(id));
        return (
          <OrderSection key={id}>
            <OrderHeading>{meta?.name || `Order ${id}`}</OrderHeading>
            {meta?.blocker ? <BlockerNote>{meta.blocker}</BlockerNote> : null}
            <activeStep.Panel orderId={id} onNext={goToNext} />
          </OrderSection>
        );
      });
    }
    return <activeStep.Panel orderId={primaryOrderId} onNext={goToNext} />;
  };

  return (
    <PoDrawerButtonStyle>
      <AddItemsPickerTheme />
      <FulfilTheme />
      <OrderDrawerShell
        open={open}
        onClose={onClose}
        title={<PortalHeader title={drawerTitle} onClose={onClose} />}
        width={activeStep ? DRAWER_WIDTH.addItems : DRAWER_WIDTH.settings}
        rootClassName={`${ADD_ITEMS_DRAWER_ROOT_CLASS} ${PO_ADD_ITEMS_DRAWER_ROOT_CLASS} ${ROOT_CLASS}`}
        styles={panelStyles}
        maskClosable
        footer={
          <AddItemsPickerFooter>
            {activeStep ? (
              <>
                <AddItemsPickerButton onClick={() => setActiveKey(null)}>
                  All steps
                </AddItemsPickerButton>
                <FooterSlot ref={setFooterNode} />
              </>
            ) : (
              <>
                <span />
                <div className="footerButtons">
                  <AddItemsPickerButton onClick={onClose}>Close</AddItemsPickerButton>
                  {showCompleteRemaining ? (
                    <AddItemsPickerButton
                      type="primary"
                      disabled={completing}
                      onClick={handleCompleteRemaining}
                    >
                      {completing ? "Working…" : "Complete remaining"}
                    </AddItemsPickerButton>
                  ) : null}
                  {nextUp ? (
                    <AddItemsPickerButton type="primary" onClick={() => setActiveKey(nextUp)}>
                      {`Start with ${visibleSteps.find(step => step.key === nextUp).title}`}
                    </AddItemsPickerButton>
                  ) : null}
                </div>
              </>
            )}
          </AddItemsPickerFooter>
        }
      >
        {activeStep ? (
          <>
            <TabBar aria-label="Fulfill steps">
              <TabList>
                {visibleSteps.map((step, index) => (
                  <Tab
                    key={step.key}
                    as="button"
                    type="button"
                    className={step.key === activeKey ? "activeItem" : undefined}
                    aria-current={step.key === activeKey ? "step" : undefined}
                    onClick={() => setActiveKey(step.key)}
                  >
                    {index + 1}. {step.title}
                    {counts && step.count(counts) > 0 ? (
                      <StepCount>{step.count(counts)}</StepCount>
                    ) : null}
                  </Tab>
                ))}
              </TabList>
            </TabBar>
            <FulfilFooterContext.Provider value={footerNode}>
              <AddItemsPickerContent $striped>
                {renderStepPanels()}
              </AddItemsPickerContent>
            </FulfilFooterContext.Provider>
          </>
        ) : (
          <AddItemsPickerContent>
            <p>
              {isMulti
                ? "Work through each step for every selected order, or use Complete remaining when your company allows it."
                : "Work through each step without leaving the order."}
            </p>
            {isFetching ? <p>Loading fulfillment status…</p> : null}
            {isMulti && batchData?.orders?.length ? (
              <OrderSummaryList>
                {batchData.orders.map(row => (
                  <li key={row.order_id}>
                    <strong>{row.name}</strong>
                    {row.blocker ? (
                      <span className="blocker">{row.blocker}</span>
                    ) : (
                      <span className="muted">Ready for fulfilment</span>
                    )}
                  </li>
                ))}
              </OrderSummaryList>
            ) : null}
            <StepCards>
              {visibleSteps.map((step, index) => (
                <StepCard
                  key={step.key}
                  type="button"
                  $nextUp={step.key === nextUp}
                  onClick={() => setActiveKey(step.key)}
                >
                  <span className="title">
                    {index + 1}. {step.title}
                    {step.key === nextUp ? <span className="next">Next up</span> : null}
                  </span>
                  <span className="description">{step.description}</span>
                  {waitingText(step) ? <span className="waiting">{waitingText(step)}</span> : null}
                </StepCard>
              ))}
            </StepCards>
          </AddItemsPickerContent>
        )}
      </OrderDrawerShell>
    </PoDrawerButtonStyle>
  );
}

const FulfilTheme = createGlobalStyle`
  .${ROOT_CLASS} .ant-drawer-content-wrapper {
    transition: width 0.25s ease;
  }
  .${ROOT_CLASS} nav[aria-label="Fulfill steps"] {
    margin: 12px 16px 0;
  }
`;

const FooterSlot = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;

  .footerButtons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-left: auto;
  }
`;

const OrderSection = styled.section`
  padding: 16px;
  border-bottom: 1px solid var(--PEARL, #ecf1f4);

  &:last-child {
    border-bottom: none;
  }
`;

const OrderHeading = styled.h3`
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: var(--DARK-GREY, #0e0e2c);
`;

const BlockerNote = styled.p`
  margin: 0 0 12px;
  color: #ad4e00;
  font-size: 13px;
`;

const OrderSummaryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 16px;

  li {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 0;
    border-bottom: 1px solid var(--PEARL, #ecf1f4);
  }

  .blocker {
    color: #ad4e00;
    font-size: 13px;
  }

  .muted {
    color: var(--ASH, #676879);
    font-size: 13px;
  }
`;

const StepCount = styled.span`
  margin-left: 8px;
  padding: 0 7px;
  border-radius: 999px;
  border: 1px solid #c5d9ff;
  background: var(--BLUE-LIGHT, #eef4ff);
  color: #1249bf;
  font-size: 12px;
  font-weight: 600;
`;

const StepCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StepCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  text-align: left;
  border: 1px solid ${({ $nextUp }) => ($nextUp ? "#2060ea" : "var(--PEARL, #ecf1f4)")};
  border-radius: 8px;
  background: ${({ $nextUp }) => ($nextUp ? "var(--BLUE-LIGHT, #eef4ff)" : "#fff")};
  padding: 14px 16px;
  cursor: pointer;

  &:hover {
    border-color: #2060ea;
    background: #f4f7ff;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: var(--DARK-GREY, #0e0e2c);
  }

  .next {
    font-size: 11px;
    font-weight: 600;
    color: #fff;
    background: #2060ea;
    border-radius: 999px;
    padding: 2px 8px;
  }

  .description {
    font-size: 13px;
    color: var(--ASH, #676879);
  }

  .waiting {
    font-size: 13px;
    color: #1249bf;
    font-weight: 500;
  }
`;
